import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { tripRoutesQuery } from "@/app/Cities/Trips/GraphQL/tripRoutes";
import { updateTrip } from "@/app/Cities/Trips/GraphQL/tripMutations.graphql";
import { Button } from "@/components/catalyst/button";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { toast } from "sonner";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { UpdateTripInput } from "@/gql/graphql";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";

export function TripEditDialog({ tripId }: { tripId: string }) {
  const { data } = useSuspenseGqlQuery(tripRoutesQuery, {
    id: tripId,
  });

  const { close } = useDialogState();

  const { mutateAsync, isPending } = useGqlMutation(updateTrip);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDistance = (kms: number) => {
    return `${kms.toLocaleString()} km`;
  };

  return (
    <BaseDialog title="Update Trip">
      <div className="space-y-4">
        {data.tripRoutes.length === 0 ? (
          <>
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
              <p className="font-medium">No routes available</p>
              <p className="mt-1">
                Google Maps couldn&apos;t find a driving route between these
                cities. Please enter the distance and duration manually.
              </p>
            </div>
            <GenericForm<UpdateTripInput>
              initialValues={{
                distance_kms: 0,
                duration_hrs: 0,
                route_name: "Manual Route",
                polyline: "",
              }}
              onSubmit={async (values) => {
                await mutateAsync({
                  id: tripId,
                  input: values,
                });

                toast.success("Trip updated successfully");
                close();
              }}
            >
              <TextInput name="route_name" label="Route Name" />
              <NumberInput name="distance_kms" label="Distance (km)" />
              <NumberInput name="duration_hrs" label="Duration (hours)" />
              <DialogSubmitButton text="Update Trip" />
            </GenericForm>
          </>
        ) : null}
        {data.tripRoutes.map((route, idx) => (
          <div key={idx} className="flex gap-4 rounded-lg border p-4">
            {route.polyline ? (
              <div className="flex-shrink-0">
                <img
                  src={route.image_url}
                  alt={`${route.name} route map`}
                  className="h-48 w-48 rounded-md object-contain"
                />
              </div>
            ) : null}

            <div className="flex flex-1 flex-col justify-between">
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">{route.name}</h3>

                <div className="space-y-2 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Distance:</span>
                    <span className="ml-2">
                      {formatDistance(route.distance_kms)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Duration:</span>
                    <span className="ml-2">
                      {formatDuration(route.duration_seconds)}
                    </span>
                  </div>
                </div>
                {!route.polyline ? (
                  <p className="text-xs text-gray-500">
                    Note: Estimated distance/duration only. No detailed route
                    available.
                  </p>
                ) : null}
              </div>

              <Button
                className="mt-4"
                isLoading={isPending}
                onClick={async () => {
                  await mutateAsync({
                    id: tripId,
                    input: {
                      polyline: route.polyline,
                      distance_kms: route.distance_kms,
                      duration_hrs: Math.ceil(route.duration_seconds / 3600),
                      route_name: route.name,
                    },
                  });

                  toast.success(`Route updated to ${route.name}`);
                  close();
                }}
              >
                Select {route.name}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </BaseDialog>
  );
}
